import { ContentState, HighLevelContentType } from '@studio/graphql-types';
import { useGraphQLQuery } from 'studio5/contexts';
import { GET_CONTENTS } from 'studio5/pages/folders/queries';
import type {
  GetContentsQuery,
  GetContentsQueryVariables,
} from 'studio5/pages/folders/__generated__/queries.types';

const LEGACY_NPS_TYPE = 'legacy_nps';
const LIMIT = 100;

/**
 * Whether the account has any legacy NPS survey, or `null` while loading.
 *
 * The backend can't filter legacy NPS apart from NPS 2.0, so this scans one
 * page and fails open (`true`) when it can't prove there are none: a stray
 * "Go to legacy NPS" button is harmless, a missing one strands the user.
 */
export default function useHasLegacyNps(): boolean | null {
  const [{ data, fetching, error }] = useGraphQLQuery<
    GetContentsQuery,
    GetContentsQueryVariables
  >({
    query: GET_CONTENTS,
    variables: {
      filter: {
        contentTypes: [HighLevelContentType.Nps],
        states: [
          ContentState.Draft,
          ContentState.Published,
          ContentState.Archived,
        ],
      },
      pageInfo: { offset: 0, limit: LIMIT },
    },
  });

  if (fetching) {
    return null;
  }

  const contents = data?.contents;
  if (error || !contents?.pageInfo) {
    return true;
  }

  const { content, pageInfo } = contents;

  return (
    content.some(({ type }) => type === LEGACY_NPS_TYPE) ||
    pageInfo.total > content.length
  );
}
